// The live tail of the event stream (RESTYLE §3 cockpit, DESIGN_SPEC §11.4.7 / §11.4.12): Sequence mode, virtualized,
// follows the newest event while you stay at the bottom; only a reader scrolling up (the wheel, a touch drag, the
// scrollbar, PageUp, Home, ↑) pauses it, never a resize or a re-measure (it re-pins to the end), and a "N new" pill
// resumes (f or End too); N counts only events the current filters would show. Nothing is scored live: rows carry
// their category chip, never a severity.
//   <StreamLive ctx />        sized by the parent's --stream-h (default min(72vh, 720px))
//   <StreamScroller items … /> the virtualized scroller the document's Sequence mode shares
//   useCategoryFilter() → { off, toggle, all }   the legend-is-the-filter state (shift-click solos a category)
//   <CategoryFilter counts total off onToggle onAll />
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Button, CATEGORIES, CategoryChip, Check, Chip, ChipGroup, Empty, Pip, Search } from "@/components/ui";
import { useClock, useHotkeys } from "@/hooks";
import { agoFine, count, plural } from "@/lib/format";
import { categoryOf, countByCategory } from "@/lib/derive/categories";
import type { Category, ReportCtx } from "@/lib/derive/types";
import type { Event } from "@/types";
import { foldEvents, namesOf, noiseOf, rowFactory, t0Of, type RowFactory } from "../derive/fold";
import { EvHead, StreamItemView, buildItems, matchRow, parseQuery, type StreamItem } from "./StreamRow";

// ---- the category legend as filter ---------------------------------------------------------------------

export function useCategoryFilter() {
  const [off, setOff] = useState<ReadonlySet<Category>>(() => new Set());
  const toggle = (c: Category, solo: boolean) =>
    setOff((cur) => {
      if (solo) {
        const isSolo = cur.size === CATEGORIES.length - 1 && !cur.has(c);
        return isSolo ? new Set() : new Set(CATEGORIES.filter((x) => x !== c));
      }
      const next = new Set(cur);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });
  return { off, toggle, all: () => setOff(new Set()) };
}

export function CategoryFilter({
  counts,
  total,
  off,
  onToggle,
  onAll,
  className,
}: {
  counts: Readonly<Record<Category, number>>;
  total: number;
  off: ReadonlySet<Category>;
  onToggle: (c: Category, solo: boolean) => void;
  onAll: () => void;
  className?: string;
}) {
  return (
    <ChipGroup label="Categories (shift-click shows one alone)" className={className}>
      <Chip all pressed={off.size === 0} count={count(total)} onClick={onAll} title="Show every category">
        all
      </Chip>
      {CATEGORIES.map((c) => (
        <CategoryChip
          key={c}
          c={c}
          pressed={!off.has(c)}
          count={count(counts[c])}
          title={`${off.has(c) ? "Show" : "Hide"} ${c} events · shift-click to show only ${c}`}
          onClick={(e) => onToggle(c, e.shiftKey)}
        />
      ))}
    </ChipGroup>
  );
}

// ---- the virtualized scroller --------------------------------------------------------------------------

type Follow = { on: boolean; set: (on: boolean) => void; fresh: number };

export function StreamScroller({
  items,
  live,
  terms,
  clock,
  onToggle,
  onDetail,
  follow,
  empty,
}: {
  items: readonly StreamItem[];
  live?: boolean;
  terms?: readonly string[];
  clock: "t0" | "utc";
  onToggle: (key: string) => void;
  onDetail: (key: string) => void;
  follow?: Follow;
  empty?: ReactNode;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const head = useRef<HTMLDivElement>(null);
  const [headH, setHeadH] = useState(24);

  useLayoutEffect(() => {
    const el = head.current;
    if (!el) return;
    setHeadH(el.offsetHeight);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setHeadH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const v = useVirtualizer({
    count: items.length,
    getScrollElement: () => scroller.current,
    estimateSize: (i) => (items[i]?.type === "detail" ? 260 : 24),
    overscan: 12,
    getItemKey: (i) => items[i]?.key ?? i,
    scrollMargin: headH,
  });

  const followOn = follow?.on ?? false;
  useLayoutEffect(() => {
    if (followOn && items.length) v.scrollToIndex(items.length - 1, { align: "end" });
  }, [followOn, items.length, v]);

  // while following, a resize or a re-measure that moves the end is re-pinned, never read as the reader scrolling up
  const body = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scroller.current;
    if (!followOn || !el || typeof ResizeObserver === "undefined") return;
    const pin = () => {
      el.scrollTop = el.scrollHeight;
    };
    const ro = new ResizeObserver(pin);
    ro.observe(el);
    if (body.current) ro.observe(body.current);
    return () => ro.disconnect();
  }, [followOn]);

  // a pause needs the reader's intent: a touch drag or the scrollbar arms it, the wheel and keys pause at once
  const intent = useRef(false);
  const pause = () => {
    const el = scroller.current;
    if (follow?.on && el && el.scrollHeight > el.clientHeight + 4) follow.set(false);
  };
  const onScroll = () => {
    const el = scroller.current;
    if (!el || !follow) return;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (gap > 80 && intent.current) pause();
    else if (gap <= 4 && !follow.on) follow.set(true);
    if (gap > 80) intent.current = false;
  };

  return (
    <div
      ref={scroller}
      className="stream-scroll relative"
      onScroll={onScroll}
      onWheel={(e) => e.deltaY < 0 && pause()}
      onTouchMove={() => (intent.current = true)}
      onPointerDown={(e) => e.target === e.currentTarget && (intent.current = true)}
      onKeyDown={(e) => ["PageUp", "Home", "ArrowUp"].includes(e.key) && pause()}
      onFocus={(e) => {
        // the rows were scrolled out of the DOM: Tab lands on the scroller, so hand focus to the first row shown
        if (e.target === e.currentTarget) e.currentTarget.querySelector<HTMLElement>(".ev[tabindex]")?.focus();
      }}
    >
      <div ref={head} className="sticky top-0 z-[3]">
        <EvHead clock={clock} />
      </div>
      {items.length === 0 && empty}
      <div ref={body} className="relative w-full" style={{ height: v.getTotalSize() }}>
        {v.getVirtualItems().map((vi) => {
          const it = items[vi.index];
          if (!it) return null;
          return (
            <div key={vi.key} data-index={vi.index} ref={v.measureElement} className="absolute top-0 left-0 w-full" style={{ transform: `translateY(${vi.start - headH}px)` }}>
              <StreamItemView item={it} live={live} terms={terms} onToggle={onToggle} onDetail={onDetail} />
            </div>
          );
        })}
      </div>
      {follow && !follow.on && (
        <div className="pausebar">
          <span>
            <b>Paused</b> · {follow.fresh ? `${count(follow.fresh)} new ${plural(follow.fresh, "event")} below` : "no new events yet"}
          </span>
          <Button size="sm" variant="primary" onClick={() => follow.set(true)} title="Jump to the newest event (f or End)">
            {follow.fresh ? `↓ ${count(follow.fresh)} new` : "↓ Latest"}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---- shared list state ---------------------------------------------------------------------------------

const toggleIn = (set: ReadonlySet<string>, key: string): ReadonlySet<string> => {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

export function useOpenState() {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [details, setDetails] = useState<ReadonlySet<string>>(() => new Set());
  return {
    expanded,
    details,
    toggle: (k: string) => setExpanded((s) => toggleIn(s, k)),
    detail: (k: string) => setDetails((s) => toggleIn(s, k)),
  };
}

/** The value, updated at most once per `ms` (trailing), so a burst of snapshots folds once. */
function useThrottled<T>(value: T, ms: number): T {
  const [shown, setShown] = useState(value);
  const last = useRef(0);
  useEffect(() => {
    if (ms <= 0) {
      setShown(value);
      return;
    }
    const t = window.setTimeout(() => {
      last.current = Date.now();
      setShown(value);
    }, Math.max(0, last.current + ms - Date.now()));
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return ms <= 0 ? value : shown;
}

/** Single-event rows (children, details) built lazily once per snapshot. */
export function useRowFactory(events: readonly Event[], version: number, opts: Parameters<typeof rowFactory>[1]): RowFactory {
  const cache = useRef<{ version: number; f: RowFactory } | null>(null);
  return (e: Event) => {
    if (!cache.current || cache.current.version !== version) cache.current = { version, f: rowFactory(events, opts) };
    return cache.current.f(e);
  };
}

// ---- the live tail -------------------------------------------------------------------------------------

export function StreamLive({ ctx }: { ctx: ReportCtx }) {
  const { stream, job } = ctx;
  const snap = stream.snap;
  const events = snap.events;
  const live = stream.live;
  const now = useClock(1000, live);
  const noise = noiseOf(ctx);
  const names = namesOf(ctx);
  const t0 = t0Of(ctx);
  const [hideBg, setHideBg] = useState(true);
  const [query, setQuery] = useState("");
  const filter = useCategoryFilter();
  const open = useOpenState();
  const [followOn, setFollowOn] = useState(true);
  const [pausedAt, setPausedAt] = useState(0);

  // Refold at most four times a second while events pour in (the fold is linear in the run's events); the
  // background index and the process names are per snapshot, so they are read at fold time, not tracked.
  const version = useThrottled(snap.version, live ? 250 : 0);
  const latest = useRef({ noise, names });
  latest.current = { noise, names };
  const rows = useMemo(
    () => foldEvents(events, { mode: "sequence", t0, noise: latest.current.noise, hideBackground: hideBg, procName: latest.current.names, sample: job.filename }),
    [events, version, t0, hideBg, job.filename],
  );
  const bySeq = useMemo(() => new Map(events.map((e) => [e.seq, e])), [events, version]);
  const make = useRowFactory(events, version, { t0, noise, procName: names, sample: job.filename });

  const q = useMemo(() => parseQuery(query), [query]);
  const shown = useMemo(() => rows.filter((r) => !filter.off.has(r.category) && matchRow(r, q)), [rows, filter.off, q]);
  const items = useMemo(
    () =>
      buildItems(shown, {
        clock: "t0",
        expanded: open.expanded,
        details: open.details,
        child: (seq) => {
          const e = bySeq.get(seq);
          return e ? make(e) : undefined;
        },
        event: (seq) => bySeq.get(seq),
      }),
    [shown, open.expanded, open.details, bySeq],
  );

  const visibleEvents = hideBg ? events.length - noise.total : events.length;
  const counts = useMemo(() => {
    const bg = latest.current.noise;
    return countByCategory(hideBg ? events.filter((e) => !bg.hits.has(e.seq)) : events);
  }, [events, version, hideBg]);

  const setFollow = (on: boolean) => {
    setFollowOn(on);
    if (!on) setPausedAt(events.length);
  };
  useHotkeys({ f: () => setFollow(true), End: () => setFollow(true) });

  // One polite summary at most every 10 s (the log itself is aria-live="off").
  const [summary, setSummary] = useState("");
  const said = useRef({ at: 0, n: events.length });
  useEffect(() => {
    if (!live) return;
    const added = events.length - said.current.n;
    if (added <= 0 || now - said.current.at < 10_000) return;
    const c = countByCategory(events.slice(-added));
    const parts = CATEGORIES.filter((k) => c[k] > 0).map((k) => `${c[k]} ${k}`);
    setSummary(`${added} new ${plural(added, "event")}: ${parts.join(", ")}`);
    said.current = { at: now, n: events.length };
  }, [now, live, events]);

  // new since the pause, counting only what the filters would show (background, categories, search)
  const fresh = useMemo(() => {
    if (followOn) return 0;
    const bg = latest.current.noise;
    let n = 0;
    for (let i = pausedAt; i < events.length; i++) {
      const e = events[i];
      if (hideBg && bg.hits.has(e.seq)) continue;
      if (filter.off.has(categoryOf(e.kind))) continue;
      if (q.text && !matchRow(make(e), q)) continue;
      n++;
    }
    return n;
    // `version` re-reads the growing events array; `make` is per snapshot
  }, [followOn, pausedAt, events, version, hideBg, filter.off, q]);
  const last = stream.rate.lastEventAt;
  const filtered = filter.off.size > 0 || q.text !== "";

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-x-5 gap-y-2.5">
        <CategoryFilter counts={counts} total={visibleEvents} off={filter.off} onToggle={filter.toggle} onAll={filter.all} />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Search value={query} onChange={setQuery} label="Filter live events" placeholder="pid:4821, host, path…" width="min(240px, 100%)" />
          <Check checked={hideBg} onChange={setHideBg} count={count(noise.total)}>
            Hide OS background
          </Check>
        </div>
      </div>
      <div className="stream mt-3" role="log" aria-live="off" aria-label="Live event stream">
        <StreamScroller
          items={items}
          live
          terms={q.terms}
          clock="t0"
          onToggle={open.toggle}
          onDetail={open.detail}
          follow={{ on: followOn, set: setFollow, fresh }}
          empty={
            <Empty inline rule={false} className="px-2.5">
              {events.length === 0
                ? "Waiting for the first event"
                : filtered
                  ? "No events match this filter yet"
                  : hideBg && noise.total
                    ? `Only OS background so far · ${count(noise.total)} hidden`
                    : "No events yet"}
            </Empty>
          }
        />
        <div className="stream-foot">
          <span>
            {live ? (
              <>
                <Pip state={stream.connection === "reconnecting" ? "lost" : last !== null && now - last > 10_000 ? "idle" : "live"} batchId={snap.batchId} className="mr-2" />
                <b>Live</b> · {stream.rate.perSec.toFixed(1)} ev/s{last !== null && <> · last event {agoFine(last, Math.max(now, last))}</>} · {followOn ? "following" : "paused"}
              </>
            ) : (
              <>
                <b>{stream.ended ? "End of stream" : "Stream stopped"}</b> · {count(events.length)} {plural(events.length, "event")}
              </>
            )}
            {hideBg && noise.total > 0 && <> · {count(noise.total)} background hidden</>}
          </span>
          <span className="tabular-nums">seq {count(snap.lastSeq)}</span>
        </div>
      </div>
      <p className="sr-only" role="status" aria-live="polite">
        {summary}
      </p>
    </div>
  );
}

